import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth-context";
import { PlanProvider } from "@/lib/use-plan";
import { ProtectedRoute, RedirectIfAuthed } from "@/components/ProtectedRoute";
import { RequireRole } from "@/components/RequireRole";
import PlanLock from "@/components/PlanLock";
import ErrorBoundary from "@/components/ErrorBoundary";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Onboarding from "@/pages/Onboarding";
import AppShell from "@/pages/AppShell";
import Dashboard from "@/pages/Dashboard";
import Items from "@/pages/Items";
import ItemNew from "@/pages/ItemNew";
import ItemDetail from "@/pages/ItemDetail";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Locations from "@/pages/Locations";
import Outfits from "@/pages/Outfits";
import OutfitNew from "@/pages/OutfitNew";
import OutfitDetail from "@/pages/OutfitDetail";
import Loans from "@/pages/Loans";
import Scan from "@/pages/Scan";
import SharedLinks from "@/pages/SharedLinks";
import PublicShare from "@/pages/PublicShare";
import Portal from "@/pages/Portal";
import DevInbox from "@/pages/DevInbox";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AuditLog from "@/pages/admin/AuditLog";
import Reports from "@/pages/Reports";
import ItemImport from "@/pages/ItemImport";
import AcceptInvite from "@/pages/AcceptInvite";
import Settings from "@/pages/settings/Settings";
import ProfileSettings from "@/pages/settings/ProfileSettings";
import TeamSettings from "@/pages/settings/TeamSettings";
import WorkspaceSettings from "@/pages/settings/WorkspaceSettings";
import BillingSettings from "@/pages/settings/BillingSettings";
import MockCheckout from "@/pages/MockCheckout";
import MockPortal from "@/pages/MockPortal";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import NotFound from "@/pages/NotFound";

export default function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <PlanProvider>
            <Toaster position="top-center" richColors closeButton />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<RedirectIfAuthed><Login /></RedirectIfAuthed>} />
              <Route path="/signup" element={<RedirectIfAuthed><Signup /></RedirectIfAuthed>} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/share/:token" element={<PublicShare />} />
              <Route path="/portal/:token" element={<Portal />} />
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/mock-checkout" element={<ProtectedRoute><MockCheckout /></ProtectedRoute>} />
              <Route path="/mock-portal" element={<ProtectedRoute><MockPortal /></ProtectedRoute>} />
              <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
              <Route path="/app" element={<ProtectedRoute requireOnboarded><AppShell /></ProtectedRoute>}>
                <Route index element={<Dashboard />} />
                <Route path="items" element={<Items />} />
                <Route path="items/new" element={<ItemNew />} />
                <Route path="items/:id" element={<ItemDetail />} />
                <Route path="clients" element={<Clients />} />
                <Route path="clients/:id" element={<ClientDetail />} />
                <Route path="locations" element={<Locations />} />
                <Route path="outfits" element={<PlanLock feature="outfits"><Outfits /></PlanLock>} />
                <Route path="outfits/new" element={<PlanLock feature="outfits"><OutfitNew /></PlanLock>} />
                <Route path="outfits/:id" element={<PlanLock feature="outfits"><OutfitDetail /></PlanLock>} />
                <Route path="loans" element={<PlanLock feature="loans"><Loans /></PlanLock>} />
                <Route path="scan" element={<PlanLock feature="scan"><Scan /></PlanLock>} />
                <Route path="reports" element={
                  <RequireRole allow={["owner", "admin"]}>
                    <PlanLock feature="reports"><Reports /></PlanLock>
                  </RequireRole>
                } />
                <Route path="import" element={
                  <RequireRole allow={["owner", "admin"]}>
                    <PlanLock feature="csv_import"><ItemImport /></PlanLock>
                  </RequireRole>
                } />
                <Route path="admin" element={<RequireRole allow={["owner", "admin"]} />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="audit-log" element={
                    <PlanLock feature="audit_log"><AuditLog /></PlanLock>
                  } />
                </Route>
                <Route path="settings" element={<Settings />}>
                  <Route index element={<Navigate to="profile" replace />} />
                  <Route path="profile" element={<ProfileSettings />} />
                  <Route path="billing" element={<BillingSettings />} />
                  <Route path="team" element={
                    <RequireRole allow={["owner", "admin"]}>
                      <PlanLock feature="team"><TeamSettings /></PlanLock>
                    </RequireRole>
                  } />
                  <Route path="workspace" element={<RequireRole allow={["owner", "admin"]}><WorkspaceSettings /></RequireRole>} />
                  <Route path="shared-links" element={<SharedLinks />} />
                </Route>
              </Route>
              <Route path="/dev/inbox" element={<DevInbox />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </PlanProvider>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
